import { configureStore } from '@reduxjs/toolkit'
import { rootReducer } from './reducer'
import { ThunkExtraArgument } from '../types/store'
import { $api, getUrl } from '../api/api'
import { getJwt, getTokenRefresh, saveJwt, saveUser, signOut, tokenRefresh } from '../utils/authentication'
import { licenseActions } from './slices/licenseSlice'
import createAuthRefreshInterceptor from 'axios-auth-refresh'
import { alertErrorMessage, throwErrorMessage } from '../utils/Errors'
import { logout } from './async-thunks/auth/logout'


// NOTE: decouple interceptors from store
$api.interceptors.request.use(
    (request: any) => {
        request.withCredentials = true
        request.headers['Authorization'] = `Bearer ${getJwt()}`
        return request
    },
    (error) => {
        return Promise.reject(error)
    }
)

$api.interceptors.response.use(
    (response) => {
        return response
    },
    async (err) => {
        if (err.config?.url?.includes('/oauth/refresh_token')) {
            throwErrorMessage('Could not refresh session. Token expired')
            store.dispatch(logout())
            window.location.reload()
        }
        if (err.response?.status === 503) {
            store.dispatch(licenseActions.licenseInvalid())
            signOut()
        }
        return Promise.reject(err)
    }
)

export const renewTokens = async () => {
    try {
        const { data: tokens } = await $api.post<{
            access_token: string
            refresh_token: string
        }>(
            getUrl('/oauth/refresh_token'),
            { refresh_token: getTokenRefresh() },
        )
        if (tokens.access_token && tokens.refresh_token) {
            saveJwt(tokens.access_token)
            tokenRefresh(tokens.refresh_token)
            saveUser(tokens)
        }
        return tokens
    } catch (e: any) {
        alertErrorMessage(e)
    }
}

export async function refreshAuth(failedRequest: any) {
    try {
        const tokens = await renewTokens()
        if (tokens?.access_token) {
            failedRequest.response.config.headers['Authorization'] =
                `Bearer ${tokens.access_token}`
        }
    } catch (e: any) {
        console.info('Token renewal failed...')
        console.error(e)
    }
}

createAuthRefreshInterceptor($api, refreshAuth, { statusCodes: [401] });


const ThunkExtraArg: ThunkExtraArgument = {
    api: $api,
}

export const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
        thunk: {
            extraArgument: ThunkExtraArg
        },
        serializableCheck: false,
        immutableCheck: true,
    }).concat()
})